import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Download } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";

const isImage = (name = "") => /\.(png|jpe?g|webp|heic|gif)$/i.test(name);

export default function PodUpload({ jobId, files = [], onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setBusy(true);
    try {
      let latest = null;
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/jobs/${jobId}/pod`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        latest = data;
      }
      toast.success(`Uploaded ${fileList.length} file${fileList.length > 1 ? "s" : ""}`);
      if (latest && onChange) onChange(latest);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const del = async (filename) => {
    if (!window.confirm("Delete this POD file?")) return;
    try {
      const { data } = await api.delete(`/jobs/${jobId}/pod/${encodeURIComponent(filename)}`);
      toast.success("POD removed");
      if (onChange) onChange(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const backendBase = API.replace(/\/api$/, "");
  const buildUrl = (u) => (u?.startsWith("http") ? u : `${backendBase}${u}`);

  return (
    <div className="border border-zinc-200 p-5 bg-white">
      <div className="flex items-center justify-between">
        <div>
          <div className="mono-label">PROOF OF DELIVERY (POD)</div>
          <p className="text-xs text-zinc-500 mt-1">PDF / JPG / PNG · max 20MB each</p>
        </div>
        <Button onClick={() => inputRef.current?.click()} disabled={busy}
          className="sharp bg-zinc-950 hover:bg-zinc-800" data-testid="pod-upload-btn">
          <Upload className="h-4 w-4 mr-2" /> {busy ? "Uploading..." : "Upload POD"}
        </Button>
        <input ref={inputRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.gif"
          onChange={(e) => upload(Array.from(e.target.files || []))} className="hidden" data-testid="pod-input" />
      </div>

      {files.length === 0 ? (
        <div className="mt-4 border border-dashed border-zinc-300 p-6 text-center mono-label">
          No POD uploaded yet
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          {files.map((f) => (
            <div key={f.filename} className="border border-zinc-200 bg-zinc-50" data-testid={`pod-file-${f.filename}`}>
              <div className="aspect-video bg-white border-b border-zinc-200 flex items-center justify-center overflow-hidden">
                {isImage(f.original_name) ? (
                  <img src={buildUrl(f.url)} alt={f.original_name} className="w-full h-full object-cover" />
                ) : (
                  <FileText className="h-10 w-10 text-zinc-400" />
                )}
              </div>
              <div className="p-2 text-xs">
                <div className="font-mono truncate" title={f.original_name}>{f.original_name}</div>
                <div className="mono-label !text-[10px] mt-1">
                  {(f.size / 1024).toFixed(0)} KB · {new Date(f.uploaded_at).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <a href={buildUrl(f.url)} target="_blank" rel="noreferrer"
                     className="text-[#0056B3] underline inline-flex items-center" data-testid={`pod-open-${f.filename}`}>
                    <Download className="h-3 w-3 mr-1" /> Open
                  </a>
                  <button onClick={() => del(f.filename)} className="ml-auto text-rose-700 inline-flex items-center"
                          data-testid={`pod-delete-${f.filename}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
